/* Cart drawer add-on rail JS
   On + Add: post variant to /cart/add.js with sections, swap cart drawer.
   No custom element wrapper needed; uses event delegation off the rail container.
*/

(function () {
  if (window.__tuffCartAddonsBound) return;
  window.__tuffCartAddonsBound = true;

  // ----------------------------------------------------------------
  // Analytics: view / click / add events on the cart-drawer add-on rail.
  // Event names use Shopify's `<namespace>:<event>` convention so Custom
  // Pixel subscribers do not drop them as un-namespaced.
  //
  // Four fire paths so the events reach whichever tool is listening:
  //   1. Shopify.analytics.publish: canonical storefront -> Custom Pixel
  //      handoff. Picks up GA4 (Custom Pixel) and any future sandboxed
  //      pixels Logan adds via Settings -> Customer events.
  //   2. document CustomEvent: belt-and-suspenders for any web-pixel that
  //      subscribes via `analytics.subscribe('tuff_cart_drawer:...', cb)`
  //      OR via a vanilla DOM listener. Survives if path 1 ever changes.
  //   3. window._learnq: Klaviyo onsite SDK, lives on the storefront page
  //      directly (no sandbox). Confirmed Connected in the app pixel
  //      audit, so events arrive in Logan's Klaviyo dashboard immediately.
  //   4. window.dataLayer: GTM convention. No-op today (Tuff Pupper has
  //      no GTM container) but ready when Logan adds Google + YouTube
  //      channel in M2.
  // Lucky Orange auto-tracks all click events natively so no manual
  // call is needed there.
  // ----------------------------------------------------------------
  const EVENT_NS = 'tuff_cart_drawer';

  function tuffTrack(suffix, props) {
    const eventName = EVENT_NS + ':' + suffix;
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, props);
      }
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent(eventName, { detail: props }));
    } catch (e) {}
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', eventName, props]);
      }
    } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, props));
    } catch (e) {}
  }

  function rowProps(row) {
    if (!row) return null;
    const cents = parseInt(row.dataset.cartAddonPrice, 10) || 0;
    return {
      list_name: 'cart_drawer_addons',
      handle: row.dataset.cartAddonHandle,
      title: row.dataset.cartAddonTitle,
      variant_id: row.dataset.cartAddonVariant,
      price_cents: cents,
      usd_price: Math.round(cents) / 100,
      currency: 'USD',
      position: parseInt(row.dataset.cartAddonPosition, 10) || 0
    };
  }

  function trackRailView(rail) {
    if (!rail || rail.dataset.tuffTracked === '1') return;
    const rows = rail.querySelectorAll('[data-cart-addon-row]');
    if (!rows.length) return;
    rail.dataset.tuffTracked = '1';
    tuffTrack('addon_view', {
      list_name: 'cart_drawer_addons',
      currency: 'USD',
      count: rows.length,
      items: Array.from(rows).map(rowProps)
    });
  }

  function scanForRails() {
    document.querySelectorAll('[data-cart-addons]').forEach(trackRailView);
  }

  // Initial scan once the DOM is ready. The rail may already be on the page
  // if the customer has items in cart and the drawer is pre-rendered.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForRails);
  } else {
    scanForRails();
  }

  // Re-scan whenever the section-rendering API swaps fresh HTML into the
  // cart drawer. Scope the observer to the cart-drawer element, not the whole
  // document body, so off-cart DOM churn from other apps (Lucky Orange, Judge.me,
  // Affiliatly) never triggers a scan. Debounced to 120ms. The callback only
  // reads the rail and fires an analytics event; its single DOM write
  // (data-tuffTracked) is guarded by trackRailView so it cannot loop.
  let _scanTimer = null;
  const railObserverTarget = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
  if (railObserverTarget) {
    new MutationObserver(function () {
      if (_scanTimer) return;
      _scanTimer = setTimeout(function () {
        _scanTimer = null;
        scanForRails();
      }, 120);
    }).observe(railObserverTarget, { childList: true, subtree: true });
  }

  // Product-name link click. No preventDefault, customer is navigating to
  // the PDP. We log the interest signal so Logan can see name-click rate
  // vs add-click rate (a soft-vs-hard intent split).
  document.addEventListener('click', function (evt) {
    const link = evt.target.closest('.cart-addons__name');
    if (!link) return;
    const row = link.closest('[data-cart-addon-row]');
    if (!row) return;
    tuffTrack('addon_click', rowProps(row));
  });

  // One add at a time across the whole rail: two concurrent /cart/add.js
  // requests can resolve out of order, and the later-FINISHING response's
  // sections payload would render a cart missing the other item.
  let addInFlight = false;

  document.addEventListener('click', function (evt) {
    const btn = evt.target.closest('[data-cart-addon-add]');
    if (!btn) return;
    if (btn.getAttribute('aria-disabled') === 'true') return;
    if (addInFlight) return;

    evt.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const row = btn.closest('[data-cart-addon-row]');

    addInFlight = true;
    setLoading(btn, true);

    const sectionsToRender = [
      'cart-drawer',
      'cart-icon-bubble',
      'cart-drawer-addons'
    ];

    const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(`${url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sectionsToRender,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.status && data.status >= 400) {
          // Shopify sends a shopper-ready sentence here, e.g. "The product ... is
          // already sold out." Showing it beats a shake nobody sees with reduce-motion on.
          showAddError(btn, data.description || data.message);
          setLoading(btn, false);
          addInFlight = false;
          return;
        }
        clearAddError();
        addInFlight = false;
        // Fire add event before swapping the cart drawer so the event
        // captures the row that drove the conversion (post-swap the row
        // is destroyed when the rail re-renders).
        tuffTrack('addon_add', rowProps(row));
        // Show success state on the clicked button BEFORE swapping cart drawer.
        setSuccess(btn);
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.classList.remove('is-empty');
        setTimeout(() => {
          if (cartDrawer && data && data.sections && typeof cartDrawer.renderContents === 'function') {
            try { cartDrawer.renderContents({ sections: data.sections }, true); }
            catch(e) { if (typeof cartDrawer.reload === 'function') cartDrawer.reload(); }
            if (typeof window.loadTemplateContent === 'function') {
              try { window.loadTemplateContent(cartDrawer); } catch(e) {}
            }
          } else if (cartDrawer && typeof cartDrawer.reload === 'function') {
            cartDrawer.reload();
          }
        }, 350);
      })
      .catch(function () {
        showAddError(btn, 'Sorry, that could not be added just now. Please try again.');
        setLoading(btn, false);
        addInFlight = false;
      });
  });

  function setLoading(btn, state) {
    const sp = btn.querySelector('.cart-addons__add-spinner');
    const txt = btn.querySelector('.cart-addons__add-text');
    if (state) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (sp) sp.classList.remove('hidden');
      if (txt) {
        txt.dataset.prevText = txt.textContent;
        txt.textContent = 'Adding';
      }
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('loading');
      if (sp) sp.classList.add('hidden');
      if (txt && txt.dataset.prevText) {
        txt.textContent = txt.dataset.prevText;
        delete txt.dataset.prevText;
      }
    }
  }

  function setSuccess(btn) {
    const sp = btn.querySelector('.cart-addons__add-spinner');
    const txt = btn.querySelector('.cart-addons__add-text');
    if (sp) sp.classList.add('hidden');
    btn.classList.remove('loading');
    btn.classList.add('success');
    if (txt) {
      txt.dataset.prevText = txt.dataset.prevText || txt.textContent;
      txt.textContent = 'Added';
    }
    setTimeout(() => {
      btn.classList.remove('success');
      btn.removeAttribute('aria-disabled');
      if (txt && txt.dataset.prevText) {
        txt.textContent = txt.dataset.prevText;
        delete txt.dataset.prevText;
      }
    }, 900);
  }

  function errorEl() {
    return document.getElementById('CartDrawer-CartErrors');
  }

  function clearAddError() {
    var el = errorEl();
    if (el) el.textContent = '';
  }

  // The shake alone is invisible to anyone with reduce-motion on, so always put the
  // reason in the drawer's existing role="alert" element as well.
  function showAddError(btn, message) {
    flashError(btn);
    var el = errorEl();
    if (el && message) el.textContent = message;
  }

  function flashError(btn) {
    if (typeof btn.animate !== 'function') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    btn.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-0.4rem)' },
        { transform: 'translateX(0.4rem)' },
        { transform: 'translateX(0)' }
      ],
      { duration: 220, easing: 'ease-in-out' }
    );
  }
})();
